import React, { lazy, memo, Suspense } from 'react';

import { HyperlinkPlaceholder } from './hyperlink-placeholder.tsx';
import { type HyperlinkReadContent } from './hyperlink-read-content.ts';
import { type HyperlinkSmartCard as SmartCardPresenter } from './hyperlink-smart-card.tsx';
import styles from './hyperlink-value-view.module.css';

const HyperlinkSmartCard = lazy<
  React.ComponentType<React.ComponentProps<typeof SmartCardPresenter>>
>(() =>
  import('./hyperlink-smart-card.tsx')
    .then((module) => ({ default: module.HyperlinkSmartCard }))
    // A failed presentation chunk must leave the underlying link usable.
    .catch(() => ({ default: UnavailableSmartCard }))
);

function UnavailableSmartCard(props: React.ComponentProps<typeof HyperlinkPlaceholder>) {
  return <HyperlinkPlaceholder {...props} asLink />;
}

/**
 * Value-level presentation. The application supplies disabled/focus policy and owns
 * the Smart Card provider; static values never load the provider-backed presenter.
 */
export const HyperlinkValueView = memo(function HyperlinkValueView({
  content,
  showDisabledValue = false,
  noFocus = false
}: {
  readonly content: HyperlinkReadContent;
  readonly showDisabledValue?: boolean;
  readonly noFocus?: boolean;
}) {
  return (
    <ul className={styles.value}>
      {content.links.map((link, index) => (
        <li key={JSON.stringify([link.key, link.url, index])}>
          {link.resolve && link.href ? (
            <Suspense
              fallback={
                <HyperlinkPlaceholder link={link} showDisabledValue={showDisabledValue} />
              }
            >
              <HyperlinkSmartCard
                link={link}
                showDisabledValue={showDisabledValue}
                noFocus={noFocus}
              />
            </Suspense>
          ) : (
            <HyperlinkPlaceholder
              link={link}
              showDisabledValue={showDisabledValue}
              noFocus={noFocus}
              asLink={Boolean(link.href)}
            />
          )}
        </li>
      ))}
    </ul>
  );
});
